import { getIdentityLoginUrl } from '@atlassian/pillar/runtime/env';

const CLIENT_AUTH_REDIRECT_COUNT_STORAGE_KEY = 'twg-microsite.auth.redirect-count';

/**
 * Sentinel set immediately before redirecting the user to the identity
 * provider in {@link attemptClientLogin}. Survives the same-tab redirect
 * via sessionStorage and is consumed by {@link consumeLoginPendingMarker}
 * the first time `fetchUserProfile` resolves successfully after the
 * redirect, so we can distinguish a real login from a session resume
 * (e.g. a page reload with a still-valid auth cookie).
 */
const LOGIN_PENDING_STORAGE_KEY = 'twg-microsite.auth.login-pending';

/**
 * Maximum number of times the user will be redirected to the auth page before instead
 * showing a login failed error.
 */
const MAX_REDIRECT_COUNT = 1;

function getRedirectCount(): number {
	const count = globalThis.window.sessionStorage.getItem(CLIENT_AUTH_REDIRECT_COUNT_STORAGE_KEY);
	return count ? parseInt(count, 10) : 0;
}

export function hasAutomaticAuthFailed(): boolean {
	return getRedirectCount() > MAX_REDIRECT_COUNT;
}

export function attemptClientLogin(): void {
	const newRedirectCount = getRedirectCount() + 1;
	globalThis.window.sessionStorage.setItem(
		CLIENT_AUTH_REDIRECT_COUNT_STORAGE_KEY,
		newRedirectCount.toString(),
	);
	// Mark that we have just initiated a login redirect. The success path
	// of `fetchUserProfile` reads + removes this to fire `track.user.loggedIn`
	// at most once per actual login (not on every reload with a valid
	// session cookie).
	globalThis.window.sessionStorage.setItem(LOGIN_PENDING_STORAGE_KEY, '1');

	globalThis.window.location.replace(getIdentityLoginUrl());
}

/**
 * Returns `true` exactly once, the first time it is called after the
 * sentinel was set by {@link attemptClientLogin}. Subsequent calls (and
 * page loads where no login was initiated) return `false`. Safe in SSR /
 * non-browser contexts (returns `false` if `sessionStorage` is unavailable).
 */
export function consumeLoginPendingMarker(): boolean {
	try {
		const pending = globalThis.window.sessionStorage.getItem(LOGIN_PENDING_STORAGE_KEY) === '1';
		if (pending) {
			globalThis.window.sessionStorage.removeItem(LOGIN_PENDING_STORAGE_KEY);
		}
		return pending;
	} catch {
		return false;
	}
}

export function cleanupClientAuthFlow(): void {
	globalThis.window.sessionStorage.removeItem(CLIENT_AUTH_REDIRECT_COUNT_STORAGE_KEY);
	globalThis.window.sessionStorage.removeItem(LOGIN_PENDING_STORAGE_KEY);
}
